import { buildAuthedImageUrl } from '@artic/api-client';
import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@artic/ui/dialog';
import { Input } from '@artic/ui/input';
import { Label } from '@artic/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@artic/ui/select';
import { Textarea } from '@artic/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@artic/ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';

import { ImageGallery } from '../../components/image-gallery';
import { usePartnerName } from '../../features/inbox/use-partner-name';
import {
  DEFAULT_EFFORT_LABEL,
  EFFORT_LABELS,
  formatCriterionName,
  formatEffortLabel,
  formatResponseTypeLabel,
} from '../../features/vision-test-plans/labels';
import { PlanImagesActions } from '../../features/vision-test-plans/plan-images-actions';
import {
  useCreateRun,
  useUpdatePlanNotes,
  visionTestPlanDetailQueryOptions,
  visionTestPlanItemsQueryOptions,
} from '../../features/vision-test-plans/queries';
import {
  RUNS_DEFAULT_LIMIT,
  visionTestPlanDetailSearchParsers,
} from '../../features/vision-test-plans/search';
import type {
  QualityCheckPrompt,
  VisionTestPlan,
  VisionTestPlanRun,
  VisionTestPlanRunStatus,
} from '../../features/vision-test-plans/types';

export const Route = createFileRoute('/vision_test_plans/$test_plan_id')({
  staticData: {
    breadcrumb: 'Plan',
    layout: 'default',
  },
  component: VisionTestPlanDetailPage,
});

const STATUS_VARIANT: Record<
  VisionTestPlanRunStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  new: 'outline',
  queued: 'secondary',
  in_progress: 'default',
  completed: 'default',
  error: 'destructive',
};

type ModelTier = 'quick' | 'standard' | 'thinking';

// ─── Photo carousel ───────────────────────────────────────────────────────────

function PlanPhotoCarousel({
  partnerName,
  testPlanId,
}: {
  partnerName: string;
  testPlanId: string;
}) {
  const { data: items } = useQuery(visionTestPlanItemsQueryOptions(partnerName, testPlanId));
  if (!items || items.length === 0) return null;

  const images = items.map((item) => {
    const encodedPath = item.storage_path.split('/').map(encodeURIComponent).join('/');
    const url =
      buildAuthedImageUrl(
        `/quality/evidence/file/${encodeURIComponent(partnerName)}/${encodedPath}`,
      ) ?? '';
    return { url, filename: item.storage_path.split('/').pop() ?? item.storage_path };
  });

  return (
    <section>
      <h2 className="mb-3 text-lg font-medium">Evidence Photos</h2>
      <ImageGallery images={images} />
    </section>
  );
}

// ─── New Run Dialog ───────────────────────────────────────────────────────────

interface PromptRow {
  label: string;
  response_type: 'yes_no' | 'text';
  prompt: string;
  model: ModelTier | '';
}

function emptyRow(): PromptRow {
  return { label: '', response_type: 'yes_no', prompt: '', model: '' };
}

function NewRunDialog({
  open,
  onOpenChange,
  partnerName,
  testPlanId,
  lastRunPrompts,
  baselinePrompts,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerName: string;
  testPlanId: string;
  lastRunPrompts: QualityCheckPrompt[] | null;
  baselinePrompts: QualityCheckPrompt[] | null;
}) {
  const createRun = useCreateRun();

  const [runName, setRunName] = useState('');
  const [rows, setRows] = useState<PromptRow[]>([emptyRow()]);

  function populateDefaults() {
    if (!baselinePrompts) return;
    setRows(
      baselinePrompts.map((p) => ({
        label: p.label,
        response_type: p.response_type,
        prompt: p.prompt,
        model: (p.model ?? '') as ModelTier | '',
      })),
    );
  }

  function populateLastRun() {
    if (!lastRunPrompts) return;
    setRows(
      lastRunPrompts.map((p) => ({
        label: p.label,
        response_type: p.response_type,
        prompt: p.prompt,
        model: (p.model ?? '') as ModelTier | '',
      })),
    );
  }

  function updateRow<K extends keyof PromptRow>(index: number, key: K, value: PromptRow[K]) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    const prompts: QualityCheckPrompt[] = rows.map((r) => ({
      label: r.label,
      response_type: r.response_type,
      prompt: r.prompt,
      ...(r.model ? { model: r.model as ModelTier } : {}),
    }));

    await createRun.mutateAsync({
      partnerName,
      testPlanId,
      ...(runName.trim() ? { name: runName.trim() } : {}),
      prompts,
    });

    setRunName('');
    setRows([emptyRow()]);
    onOpenChange(false);
  }

  const canSubmit = rows.length > 0 && rows.every((r) => r.label.trim() && r.prompt.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[60vw] max-w-none flex-col overflow-hidden sm:max-w-none">
        <DialogHeader>
          <DialogTitle>New Test Run</DialogTitle>
          <DialogDescription>
            Set up the Acceptance Criteria and model for this Run, then submit to queue it
            immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto pr-1">
          {/* Run name */}
          <div className="space-y-1.5">
            <Label htmlFor="run-name">Run name (optional)</Label>
            <Input
              id="run-name"
              placeholder="e.g. stricter wording v2"
              value={runName}
              onChange={(e) => {
                setRunName(e.target.value);
              }}
            />
          </div>

          {/* Populate helpers */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={populateDefaults}
              disabled={!baselinePrompts}
            >
              Insert Standard Acceptance Criteria
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={populateLastRun}
              disabled={!lastRunPrompts}
            >
              Insert the last Run's Acceptance Criteria
            </Button>
          </div>

          {/* Criterion rows */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Acceptance Criteria</p>
              <Button type="button" variant="outline" size="sm" onClick={addRow}>
                + Add Criterion
              </Button>
            </div>

            {rows.map((row, i) => (
              <div key={i} className="border-border space-y-3 rounded border p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-muted-foreground text-xs font-medium">Criterion {i + 1}</p>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        removeRow(i);
                      }}
                      className="text-muted-foreground hover:text-foreground text-xs"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Label</Label>
                    <Input
                      placeholder="e.g. Well lit"
                      value={row.label}
                      onChange={(e) => {
                        updateRow(i, 'label', e.target.value);
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Response type</Label>
                    <Select
                      value={row.response_type}
                      onValueChange={(v) => {
                        updateRow(i, 'response_type', v as 'yes_no' | 'text');
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes_no">Yes / No</SelectItem>
                        <SelectItem value="text">Text</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">What to check for</Label>
                  <Textarea
                    placeholder="Describe what a pass looks like, and what should fail…"
                    value={row.prompt}
                    rows={3}
                    onChange={(e) => {
                      updateRow(i, 'prompt', e.target.value);
                    }}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Effort</Label>
                  <Select
                    value={row.model || 'none'}
                    onValueChange={(v) => {
                      updateRow(i, 'model', v === 'none' ? '' : (v as ModelTier));
                    }}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{DEFAULT_EFFORT_LABEL}</SelectItem>
                      <SelectItem value="quick">{EFFORT_LABELS.quick}</SelectItem>
                      <SelectItem value="standard">{EFFORT_LABELS.standard}</SelectItem>
                      <SelectItem value="thinking">{EFFORT_LABELS.thinking}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="border-border border-t pt-4">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!canSubmit || createRun.isPending}
          >
            {createRun.isPending ? 'Creating…' : 'Create & Start Run'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Info icon ────────────────────────────────────────────────────────────────

function InfoIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-muted-foreground shrink-0"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

// ─── Outcome cell ─────────────────────────────────────────────────────────────

function RunOutcomeCell({
  run,
  plan,
}: {
  run: VisionTestPlanRun;
  plan: Pick<VisionTestPlan, 'expected_outcome_all_pass' | 'expected_outcome_all_fail'>;
}) {
  if (run.status !== 'completed') {
    return <span className="text-muted-foreground">—</span>;
  }
  if (plan.expected_outcome_all_pass) {
    return run.failed_items > 0 ? (
      <span className="text-destructive font-medium">{run.failed_items} failed</span>
    ) : (
      <span className="font-medium text-green-700">All passed</span>
    );
  }
  return run.passed_items > 0 ? (
    <span className="text-destructive font-medium">{run.passed_items} passed</span>
  ) : (
    <span className="font-medium text-green-700">All failed</span>
  );
}

// ─── Notes editor ────────────────────────────────────────────────────────────

function NotesEditor({
  partnerName,
  testPlanId,
  initialNotes,
}: {
  partnerName: string;
  testPlanId: string;
  initialNotes: string | null;
}) {
  const [draft, setDraft] = useState(initialNotes ?? '');
  const updateNotes = useUpdatePlanNotes();

  // Re-sync when the plan reloads (e.g. after another save)
  useEffect(() => {
    setDraft(initialNotes ?? '');
  }, [initialNotes]);

  const isDirty = draft !== (initialNotes ?? '');

  return (
    <div className="space-y-2">
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Add notes…"
        rows={3}
        className="text-sm"
      />
      {isDirty && (
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={updateNotes.isPending}
            onClick={() => void updateNotes.mutateAsync({ partnerName, testPlanId, notes: draft })}
          >
            {updateNotes.isPending ? 'Saving…' : 'Save notes'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDraft(initialNotes ?? '')}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function VisionTestPlanDetailPage() {
  const { test_plan_id: testPlanId } = Route.useParams();
  const partnerName = usePartnerName();
  const [{ runsPage }, setSearch] = useQueryStates(visionTestPlanDetailSearchParsers);
  const safeRunsPage = runsPage >= 1 ? runsPage : 1;

  const [newRunOpen, setNewRunOpen] = useState(false);

  const { data: plan, isLoading } = useQuery(
    visionTestPlanDetailQueryOptions(partnerName, testPlanId, {
      runsPage: safeRunsPage,
      runsLimit: RUNS_DEFAULT_LIMIT,
    }),
  );

  const { data: planItems } = useQuery(
    visionTestPlanItemsQueryOptions(partnerName ?? '', testPlanId),
  );

  const baselinePrompts: QualityCheckPrompt[] | null = (() => {
    const raw = planItems?.[0]?.original_ai_prompt;
    if (!raw) return null;
    try {
      return JSON.parse(raw) as QualityCheckPrompt[];
    } catch {
      return null;
    }
  })();

  if (isLoading || !plan) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground text-sm">Loading…</p>
      </div>
    );
  }

  const activeFilters = [
    plan.filter_operation_name ? { label: 'Operation', value: plan.filter_operation_name } : null,
    plan.filter_check_name ? { label: 'Check', value: plan.filter_check_name } : null,
    plan.filter_acceptance_criteria
      ? { label: 'Acceptance Criteria', value: plan.filter_acceptance_criteria }
      : null,
    plan.filter_date_from || plan.filter_date_to
      ? {
          label: 'Date',
          value: [plan.filter_date_from, plan.filter_date_to].filter(Boolean).join(' → '),
        }
      : null,
  ].filter((f): f is { label: string; value: string } => f !== null);

  const lastRun = plan.runs[0] ?? null;
  const runsMeta = plan.runsMeta;
  const totalRunPages = runsMeta.total > 0 ? Math.ceil(runsMeta.total / RUNS_DEFAULT_LIMIT) : 1;
  const hasRunsPrev = safeRunsPage > 1;
  const hasRunsNext = safeRunsPage < totalRunPages;

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="space-y-3">
        <Link
          to="/vision_test_plans"
          className="text-muted-foreground inline-block text-xs hover:underline"
        >
          ← Vision Test Plans
        </Link>
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-2xl font-semibold">{plan.name}</h1>
          {partnerName && (
            <PlanImagesActions
              partnerName={partnerName}
              testPlanId={testPlanId}
              totalItems={plan.total_items}
            />
          )}
        </div>
        {partnerName && (
          <NotesEditor
            partnerName={partnerName}
            testPlanId={testPlanId}
            initialNotes={plan.notes ?? null}
          />
        )}
      </header>

      {/* Photo carousel */}
      {partnerName && <PlanPhotoCarousel partnerName={partnerName} testPlanId={testPlanId} />}

      {/* Plan metadata */}
      <div className="bg-muted/40 border-border flex flex-wrap gap-6 rounded border p-4">
        <div>
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Items
          </p>
          <p className="mt-0.5 font-semibold">{plan.total_items}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Created
          </p>
          <p className="mt-0.5 text-sm">
            {formatDistanceToNow(new Date(plan.created_at), { addSuffix: true })}
          </p>
        </div>
        {activeFilters.length > 0 && (
          <div>
            <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
              Filters
            </p>
            <div className="mt-1 flex flex-col gap-1">
              {activeFilters.map((f) => (
                <div key={f.label} className="flex items-center gap-2 text-sm">
                  <Badge variant="secondary" className="text-xs">
                    {f.label}
                  </Badge>
                  <span className="text-muted-foreground">{f.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(plan.expected_outcome_all_pass || plan.expected_outcome_all_fail) && (
          <div>
            <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
              Expected outcome
            </p>
            <div className="mt-1">
              {plan.expected_outcome_all_pass && (
                <Badge
                  variant="secondary"
                  className="bg-green-100 text-xs text-green-800 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-200"
                >
                  Should all pass
                </Badge>
              )}
              {plan.expected_outcome_all_fail && (
                <Badge variant="destructive" className="text-xs">
                  Should all fail
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Runs */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-medium">
            Runs
            {runsMeta.total > 0 && (
              <span className="text-muted-foreground ml-2 text-sm font-normal">
                ({runsMeta.total})
              </span>
            )}
          </h2>
          <Button
            size="sm"
            onClick={() => {
              setNewRunOpen(true);
            }}
          >
            New Run
          </Button>
        </div>

        {plan.runs.length === 0 && safeRunsPage === 1 ? (
          <div className="border-border rounded border border-dashed p-8 text-center">
            <p className="text-muted-foreground text-sm">
              No runs yet. Click{' '}
              <button
                className="font-semibold underline underline-offset-2"
                onClick={() => setNewRunOpen(true)}
              >
                New Run
              </button>{' '}
              to start one.
            </p>
          </div>
        ) : (
          <>
            <TooltipProvider>
              <div className="border-border overflow-hidden rounded border">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-muted-foreground text-left">
                    <tr>
                      <th className="px-4 py-2 font-medium">Name</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 text-right font-medium">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex cursor-help items-center gap-1">
                              Progress <InfoIcon />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Photos processed by the AI out of the total photos in this run
                          </TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="px-4 py-2 text-right font-medium">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex cursor-help items-center gap-1">
                              Pass <InfoIcon />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Photos where the AI determined the quality checks passed
                          </TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="px-4 py-2 text-right font-medium">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex cursor-help items-center gap-1">
                              Fail <InfoIcon />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Photos where the AI determined the quality checks failed, or encountered
                            an error
                          </TooltipContent>
                        </Tooltip>
                      </th>
                      {(plan.expected_outcome_all_pass || plan.expected_outcome_all_fail) && (
                        <th className="px-4 py-2 font-medium">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex cursor-help items-center gap-1">
                                Outcome <InfoIcon />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              Whether results match this plan's expected outcome
                            </TooltipContent>
                          </Tooltip>
                        </th>
                      )}
                      <th className="px-4 py-2 font-medium">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex cursor-help items-center gap-1">
                              Review <InfoIcon />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Human review verdicts: ✓ agreed with AI result, ✗ disagreed with AI
                            result, ? processed but not yet reviewed
                          </TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="px-4 py-2 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.runs.map((run) => {
                      const pct =
                        run.total_items && run.total_items > 0
                          ? Math.min(100, Math.round((run.completed_items / run.total_items) * 100))
                          : 0;
                      const displayedCompleted =
                        run.total_items != null
                          ? Math.min(run.completed_items, run.total_items)
                          : run.completed_items;

                      return (
                        <tr key={run.run_id} className="border-border border-t">
                          <td className="px-4 py-2">
                            <Link
                              to="/vision_test_plans/$test_plan_id/runs/$run_id"
                              params={{ test_plan_id: testPlanId, run_id: run.run_id }}
                              className="text-foreground font-medium hover:underline"
                            >
                              {run.name ?? (
                                <span className="text-muted-foreground">Unnamed run</span>
                              )}
                            </Link>
                          </td>
                          <td className="px-4 py-2">
                            <Badge
                              variant={
                                STATUS_VARIANT[run.status as VisionTestPlanRunStatus] ?? 'outline'
                              }
                            >
                              {run.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">
                            {run.total_items != null && run.total_items > 0 ? (
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-muted-foreground text-xs">
                                  {displayedCompleted}/{run.total_items}
                                </span>
                                <div className="bg-muted h-1.5 w-16 overflow-hidden rounded-full">
                                  <div
                                    className={`h-full rounded-full ${run.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right text-green-700 tabular-nums">
                            {run.passed_items}
                          </td>
                          <td className="text-destructive px-4 py-2 text-right tabular-nums">
                            {run.failed_items}
                          </td>
                          {(plan.expected_outcome_all_pass || plan.expected_outcome_all_fail) && (
                            <td className="px-4 py-2 text-sm">
                              <RunOutcomeCell run={run} plan={plan} />
                            </td>
                          )}
                          <td className="px-4 py-2 text-xs">
                            {run.correct_count + run.incorrect_count + run.unreviewed_count > 0 ? (
                              <span className="flex items-center gap-1.5 tabular-nums">
                                <span className="text-green-700">{run.correct_count}✓</span>
                                <span className="text-destructive">{run.incorrect_count}✗</span>
                                {run.unreviewed_count > 0 && (
                                  <span className="text-muted-foreground">
                                    {run.unreviewed_count}?
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="text-muted-foreground px-4 py-2 text-xs">
                            {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </TooltipProvider>

            {totalRunPages > 1 && (
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Page {safeRunsPage} of {totalRunPages} · {runsMeta.total} runs
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasRunsPrev}
                    onClick={() => void setSearch({ runsPage: safeRunsPage - 1 })}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!hasRunsNext}
                    onClick={() => void setSearch({ runsPage: safeRunsPage + 1 })}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Baseline prompts from items endpoint */}
      <BaselinePromptsSection partnerName={partnerName ?? ''} testPlanId={testPlanId} />

      {partnerName && (
        <NewRunDialog
          open={newRunOpen}
          onOpenChange={setNewRunOpen}
          partnerName={partnerName}
          testPlanId={testPlanId}
          lastRunPrompts={lastRun?.prompts ?? null}
          baselinePrompts={baselinePrompts}
        />
      )}
    </div>
  );
}

// ─── Baseline Prompts ─────────────────────────────────────────────────────────

function BaselinePromptsSection({
  partnerName,
  testPlanId,
}: {
  partnerName: string;
  testPlanId: string;
}) {
  const { data: items } = useQuery(visionTestPlanItemsQueryOptions(partnerName, testPlanId));
  if (!items || items.length === 0) return null;

  const firstItem = items[0];
  if (!firstItem?.original_ai_prompt) return null;

  let prompts: QualityCheckPrompt[] | null = null;
  try {
    prompts = JSON.parse(firstItem.original_ai_prompt) as QualityCheckPrompt[];
  } catch {
    return null;
  }

  if (!prompts || prompts.length === 0) return null;

  return (
    <section>
      <h2 className="mb-3 text-lg font-medium">Standard Acceptance Criteria</h2>
      <p className="text-muted-foreground mb-3 text-sm">
        The Standard Acceptance Criteria captured from the first Evidence Item when this Test Plan
        was created.
      </p>
      <div className="flex flex-col gap-2">
        {prompts.map((p, i) => (
          <div key={i} className="border-border rounded border p-3">
            <div className="mb-1 flex items-center gap-2">
              <span className="text-sm font-medium">{formatCriterionName(p.label)}</span>
              <Badge variant="secondary" className="text-xs">
                {formatResponseTypeLabel(p.response_type)}
              </Badge>
              {p.model && (
                <Badge variant="outline" className="text-xs">
                  {formatEffortLabel(p.model)}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm">{p.prompt}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
